// Race state — the meta-layer that knows whether we're pre-race,
// racing, on the cool-down lap, or finished. Lives on world.raceState
// and is updated by main.js's tick loop.
//
// V1 scope: phase machine, target lap count, race-mode marker
// (god/solo) plus the locked solo-driver index, and finish-order
// bookkeeping. Slots reserved for flag state (green/yellow/red),
// safety car, etc. — those land later but the schema is laid out
// now so consumers can read raceState.flag without checking for it.
//
// Pure module: no DOM, no timers, no globals. Mutated in place from
// the tick loop, read everywhere else.

// Phases. The car-level cool-down behavior (slow to ~50 % pace,
// pull into pit) is gated on raceState.phase === 'cooldown', so a
// per-car flag isn't needed yet — every car flips together when the
// leader takes the checkered.
//
//   pre-race  — world is built, grid is set, sim is paused. The race
//               page UI shows the mode/lap-count picker. No tick
//               progresses physics in this phase.
//   racing    — green flag, normal racing. Physics ticks run.
//   cooldown  — leader has taken the checkered. Cars finish their
//               current lap; AI controllers slow to ~50 % pace and
//               aim for pit entry. (Phase 2 work — this constant
//               exists so consumers can branch on it today.)
//   finished  — every car has crossed S/F after the leader, or
//               retired (totaled). Results modal shows.
export const RACE_PHASES = Object.freeze({
  PRE_RACE: 'pre-race',
  RACING:   'racing',
  COOLDOWN: 'cooldown',
  FINISHED: 'finished',
});

export const RACE_MODES = Object.freeze({
  GOD:  'god',     // player can take over any car
  SOLO: 'solo',    // player is locked to a single driver (team mode)
});

// V1 default. Hard-coded per the user's call ("hardcode something
// like 20"); future revisions can read this off the track JSON.
export const DEFAULT_RACE_LAPS = 30;

// Full-course caution timing. When at least one car retires under
// green, the race goes yellow for a base period (track marshals
// pretending to deploy) plus an extra slug per incident car (cleanup
// time). Capped so a five-car pile-up doesn't stop the show for ten
// minutes. Numbers are in sim seconds.
const CAUTION_BASE_S         = 120;
const CAUTION_PER_INCIDENT_S = 30;
const CAUTION_MAX_S          = 240;

// Restart geometry. The cleanup timer (`endsAt`) is now the EARLIEST
// the caution can lift — actual restart waits until the on-track
// leader reaches a green-flag zone defined relative to S/F. Without
// this, the flag drops at an arbitrary point on track (wherever the
// leader happens to be when the timer expires) and the field is
// suddenly racing mid-lap. Real-world restarts always finish the
// caution lap and resume green AT (or just before) S/F, which is what
// CAUTION_GREEN_FLAG_LEAD_M reproduces.
//
// Warning is lap-based: fire when the leader crosses S/F to begin a
// lap that will END green (i.e. the cleanup timer will expire before
// the leader reaches the green-flag zone of that same lap). Gives the
// player roughly a full pace lap of advance notice — much better than
// the previous distance-based 150 m / ~7 s window, which fired far too
// late for teams to react.
const CAUTION_GREEN_FLAG_LEAD_M = 90;

// Restart no-overtake grace. The instant the flag goes green, every
// follower's target speed jumps from the gap-control cap (~20 m/s) up
// to full racing pace. The P-controller responses aren't perfectly
// synchronized, so transient closing deltas (a couple of m/s for a
// half-second as cars accelerate at slightly different rates) trip
// the AI's racing-pass branch — followers commit a full lateral
// offset to swerve around the car in front, and a tight pack
// collapses into wall hits and pile-ups. The grace window suppresses
// the racing-pass / much-slower overtake checks for a few seconds
// after green so cars accelerate in formation before they're allowed
// to pass. 3 s ≈ 150 m at racing pace — covers the front-straight
// melee and entry to the first corner.
const CAUTION_GRACE_PERIOD_S    = 3;

// Leader pace cap during caution. ~20 m/s ≈ 45 mph — the "safety car
// pace" that lets the field bunch up without anyone losing a lap to
// the cleanup window. The AI controller treats this as an upper bound
// on the leader's target speed; followers run a gap-controlled target
// (see CAUTION_TARGET_GAP_M etc. in ai-controller.js) keyed off their
// snapshot-rank predecessor stamped here as `_cautionPredIdx`.
const CAUTION_PACE_SPEED_MS    = 20;

// Build the initial state for a freshly-mounted race. Mode + driver
// + lap count are all set when the user clicks Start on the pre-
// race overlay (see startRace below); creation just stages the
// pre-race phase so the sim can sit paused at the grid.
export function createRaceState() {
  return {
    phase:        RACE_PHASES.PRE_RACE,
    flag:         'green',
    totalLaps:    DEFAULT_RACE_LAPS,
    raceMode:     RACE_MODES.GOD,
    soloCarIdx:   null,
    startedAt:    null,    // sim seconds at green flag
    finishedAt:   null,    // sim seconds when leader took checkered
    finishOrder:  [],      // car indices that crossed the line, in
                           // order of crossing. ACTIVE finishers only;
                           // retirees go into retiredCars instead so
                           // the final classification ranks them by
                           // last lap+arc, not by crossing order.
    retiredCars:  new Map(),  // carIdx → { lap, arc, simTime } at the
                              // tick the car was first detected out
                              // (totaled / blown / fuel). Sorted at
                              // results time: more laps + further arc
                              // ranks higher among DNFs.
    finishedCars: new Set(),  // union of finishOrder + retiredCars,
                              // used for the FINISHED-phase trip.
    // Full-course caution. `active=true` means the race is under
    // yellow — a car retired under green and the field is in cleanup.
    // `incidents` is the list of cars whose retirement triggered or
    // extended this caution; on caution end they are recovered (set
    // `_recovered=true`, removed from the visible field). `endsAt` is
    // the sim-time the caution lifts; advanceRaceState extends it as
    // additional retirements pile in. Phase (b) of the FCC work adds
    // pace caps + bunching; (c) adds the one-to-go warning. For now
    // the caution is just a flag flip + cleanup timer.
    // Caution state machine. Three phases run sequentially while
    // `active` is true:
    //   1. CLEANUP — wrecks are still on track. simTime < endsAt.
    //   2. WARN-PENDING — cleanup timer expired, wrecks have been
    //      recovered (`_recovered=true`, vanish from render + traffic).
    //      We're waiting for the on-track leader to cross S/F so we
    //      can announce "GREEN NEXT TIME BY".
    //   3. WARNING LAP — the warn has fired. The leader pace-laps the
    //      next full lap; green drops as they near S/F again.
    // `baseLeaderLap` is captured at phase 1→2 (leader's lap when
    // wrecks recover); `warnLeaderLap` at phase 2→3 (the warning lap).
    // -1 means the phase hasn't started yet.
    caution:      {
      active:        false,
      startedAt:     null,
      endsAt:        null,
      incidents:     [],   // [{ carIdx, simTime }]
      warned:        false,
      baseLeaderLap: -1,
      warnLeaderLap: -1,
      warnedAt:      null,
    },
    // Sim-time until which the AI suppresses overtake decisions on a
    // green-flag restart. 0 = no grace active. See CAUTION_GRACE_PERIOD_S.
    greenFlagGraceUntil: 0,
  };
}

// Trigger a fresh caution or extend the active one. Called from the
// retirement-detection loop in advanceRaceState whenever a car newly
// retires under green. Cleanup time = base + per-incident, capped at
// the absolute max.
function triggerOrExtendCaution(raceState, carIdx, simTime) {
  const c = raceState.caution;
  if (!c.active) {
    c.active        = true;
    c.startedAt     = simTime;
    c.incidents     = [{ carIdx, simTime }];
    c.warned        = false;
    c.baseLeaderLap = -1;
    c.warnLeaderLap = -1;
    c.warnedAt      = null;
    raceState.flag = 'yellow';
  } else {
    c.incidents.push({ carIdx, simTime });
  }
  const prevEndsAt = c.endsAt;
  const cleanup = Math.min(
    CAUTION_MAX_S,
    CAUTION_BASE_S + CAUTION_PER_INCIDENT_S * c.incidents.length,
  );
  c.endsAt = c.startedAt + cleanup;
  // Reset the green-next-time-by warning if this extension pushed the
  // end back. Without this, an incident that lands after the warn has
  // already fired for the previous timer would suppress the next emission
  // even though the field now has fresh time to wait. Re-arming the
  // lap-check sentinel re-evaluates the predicate immediately at the
  // leader's current state.
  if (prevEndsAt != null && c.endsAt > prevEndsAt) {
    // Re-arm the warn pipeline. baseLeaderLap stays untouched if
    // wrecks were already recovered (no need to redo recovery), but
    // warned + warnLeaderLap + warnedAt reset so the next-S/F crossing
    // after the new endsAt re-evaluates "GREEN NEXT TIME BY".
    c.warned        = false;
    c.warnLeaderLap = -1;
    c.warnedAt      = null;
    if (simTime < c.endsAt) {
      // Cleanup phase has been re-entered (the new endsAt is in the
      // future). Drop the captured baseline so it's re-stamped when
      // the new cleanup completes.
      c.baseLeaderLap = -1;
    }
  }
}

// Stamp per-car caution targets. Leader gets the pace-speed cap;
// followers get their predecessor's current speed. The AI controller
// reads `_cautionPaceTarget` and uses it as a floor on minCornerSpeed
// (i.e. an additional cap). Cars in pit / retired / recovered are
// skipped — they're not part of the running order and shouldn't be
// pacing or paced. Manual cars get the stamp too, but the keyboard
// controller doesn't read it (player manages own pace; the stamp is
// inert for them).
function applyCautionPace(raceState, world) {
  const cars = world.cars;
  const racingLine = world.geometry?.racingLine;
  const totalLen   = racingLine?.totalLength ?? 0;
  // Normalize arc to S/F so the wraparound at the racing line's seam
  // (its dense[0] point, which sits wherever the author started the
  // waypoints — generally NOT at S/F) coincides with the lap-number
  // increment. Without this, a car crossing the seam keeps the same
  // lapNumber but drops rawArc by ≈ totalLen, so its sort key
  // collapses by totalLen and it briefly appears LAST in the
  // standings. The car gets re-stamped as a high-rank follower, the
  // drop-back fires, and the bunch checks up at S/F. arcSF is cached
  // on the racing line at world build (see track-geometry.js).
  const arcSF = racingLine?.arcSF ?? 0;
  const order = [];
  for (let i = 0; i < cars.length; i++) {
    const c = cars[i];
    if (c._recovered) continue;
    if (raceState.retiredCars.has(i)) continue;
    if ((c.pitState?.phase ?? 'racing') !== 'racing') continue;
    const lap = c.lapState?.lapNumber ?? 0;
    const arcRaw = c.derived?.racingLineArc ?? 0;
    const arc = totalLen > 0
      ? ((arcRaw - arcSF + totalLen) % totalLen)
      : 0;
    order.push({ idx: i, key: lap * Math.max(totalLen, 1) + arc });
  }
  order.sort((a, b) => b.key - a.key);
  // Wipe stamps on every car first — a car that was paced last tick
  // but has since pitted / retired / been recovered must not carry
  // a stale stamp into its new context.
  for (const c of cars) {
    c._cautionPaceTarget = null;
    c._cautionLeader     = false;
    c._cautionRank       = null;
    c._cautionPredIdx    = null;
  }
  for (let p = 0; p < order.length; p++) {
    const car = cars[order[p].idx];
    car._cautionRank = p;
    if (p === 0) {
      // Leader: simple pace cap, no predecessor.
      car._cautionPaceTarget = CAUTION_PACE_SPEED_MS;
      car._cautionLeader     = true;
      car._cautionPredIdx    = null;
    } else {
      // Followers: AI uses gap-control against `_cautionPredIdx` and
      // ignores `_cautionPaceTarget`. Leaving the latter null keeps
      // the AI's leader-cap branch from firing on followers.
      car._cautionPaceTarget = null;
      car._cautionPredIdx    = order[p - 1].idx;
    }
  }
}

// On-track leader for caution purposes — same filter as
// applyCautionPace (no pit, no retired, no recovered). Returns the
// car's index in world.cars, or -1 if the field is empty. Used by
// the green-flag-zone end check to find whose position drives the
// restart.
function findCautionLeaderIdx(raceState, world) {
  const cars = world.cars;
  const racingLine = world.geometry?.racingLine;
  const totalLen = racingLine?.totalLength ?? 0;
  const arcSF    = racingLine?.arcSF ?? 0;
  let bestIdx = -1, bestKey = -Infinity;
  for (let i = 0; i < cars.length; i++) {
    const c = cars[i];
    if (c._recovered) continue;
    if (raceState.retiredCars.has(i)) continue;
    if ((c.pitState?.phase ?? 'racing') !== 'racing') continue;
    const lap    = c.lapState?.lapNumber ?? 0;
    const arcRaw = c.derived?.racingLineArc ?? 0;
    const arc    = totalLen > 0 ? ((arcRaw - arcSF + totalLen) % totalLen) : 0;
    const key    = lap * Math.max(totalLen, 1) + arc;
    if (key > bestKey) { bestKey = key; bestIdx = i; }
  }
  return bestIdx;
}

// Distance (m) along the racing line from `car` to S/F, going
// forward. 0 means the car just crossed S/F; totalLen means just
// before S/F. Returns Infinity if the racing line / cache is
// unavailable.
function distanceToSF(car, world) {
  const racingLine = world.geometry?.racingLine;
  if (!racingLine || !racingLine.totalLength) return Infinity;
  const totalLen = racingLine.totalLength;
  const arcSF    = racingLine.arcSF ?? 0;
  const arcRaw   = car.derived?.racingLineArc ?? 0;
  const normArc  = ((arcRaw - arcSF + totalLen) % totalLen);
  return totalLen - normArc;
}

function clearCautionPace(world) {
  for (const c of world.cars) {
    c._cautionPaceTarget = null;
    c._cautionLeader     = false;
    c._cautionRank       = null;
    c._cautionPredIdx    = null;
  }
}

// Transition pre-race → racing. Locks in the player's mode + lap
// count; the world starts running on the next tick.
export function startRace(raceState, opts) {
  if (raceState.phase !== RACE_PHASES.PRE_RACE) return false;
  const { raceMode, soloCarIdx, totalLaps, simTime } = opts || {};
  if (raceMode === RACE_MODES.SOLO && (soloCarIdx == null || soloCarIdx < 0)) {
    // Solo without a driver pick is an authoring error; refuse to
    // start so the host UI surfaces the missing field.
    return false;
  }
  raceState.raceMode   = raceMode === RACE_MODES.SOLO ? RACE_MODES.SOLO : RACE_MODES.GOD;
  raceState.soloCarIdx = raceState.raceMode === RACE_MODES.SOLO ? soloCarIdx : null;
  if (Number.isFinite(totalLaps) && totalLaps > 0) {
    raceState.totalLaps = Math.floor(totalLaps);
  }
  raceState.phase     = RACE_PHASES.RACING;
  raceState.startedAt = simTime ?? 0;
  return true;
}

// Per-tick advance. Call after lap-timing has run for every car (so
// lapNumber reflects this tick's S/F crossings). The race ends in
// two layers:
//
//   1. Leader finish — when the leader's lapNumber reaches
//      totalLaps + 1 (i.e. they've crossed S/F to start what would
//      be lap totalLaps + 1, meaning they've completed totalLaps
//      flying laps). At that moment we flip phase → cooldown,
//      stamp finishedAt, and append the leader to finishOrder.
//   2. Trailing cars — once the leader has finished, every other
//      car's NEXT S/F crossing also counts as their finish, regardless
//      of how many laps they're down. They get appended to finishOrder
//      in the order they cross. When all cars are accounted for,
//      phase → finished.
//
// Standings ordering follows the existing arc-based sort so the
// leader is unambiguous; the function reads it as the index of the
// car with the largest cumulative arc.
export function advanceRaceState(raceState, world) {
  if (raceState.phase === RACE_PHASES.PRE_RACE
   || raceState.phase === RACE_PHASES.FINISHED) return;

  const cars = world.cars;
  if (!cars || cars.length === 0) return;

  // Un-retirement: while the race is still green, a car that
  // previously tripped a retirement condition but has since recovered
  // (typically via a pit stop — repair clears damage, refuel restores
  // fuel, tire change clears blownTires) gets pulled back into
  // contention. Without this, a player who blew a tire on lap 8,
  // limped to the box, took fresh rubber, and finished the race
  // normally still ends up classified DNF — they got 3rd by lap+arc
  // but were ranked 4th in results because the eager-retirement
  // stamp put them in the DNF bucket forever.
  //
  // Only valid during RACING — once the leader takes the checkered
  // and we move to COOLDOWN, a car that retired before then is
  // genuinely a DNF for results purposes (they didn't complete the
  // race distance under green). And finishers in finishOrder are
  // never reconsidered (they crossed the line cleanly).
  if (raceState.phase === RACE_PHASES.RACING) {
    for (const idx of Array.from(raceState.retiredCars.keys())) {
      const c    = cars[idx];
      const cond = c?.condition;
      if (!cond) continue;
      const stillRetired = cond.damage > 0.5 || cond.fuel <= 0 || cond.blownTires === true;
      if (stillRetired) continue;
      raceState.retiredCars.delete(idx);
      raceState.finishedCars.delete(idx);
      c._raceFinished = false;
      c._retired      = false;
    }
  }

  // Retirement detection runs every tick (racing AND cooldown). A
  // car becomes "out" when totaled, blown, or out of fuel — at that
  // moment we stamp the lap+arc they were at, so the final
  // classification can rank DNFs by progress (most laps first).
  // Without this, a car that blew up on lap 5 would rank by
  // index order in the cooldown sweep and end up classified above
  // a car that retired on lap 19 — wrong.
  for (let i = 0; i < cars.length; i++) {
    if (raceState.finishedCars.has(i)) continue;
    const c    = cars[i];
    const cond = c.condition;
    const retired = cond && (cond.damage > 0.5 || cond.fuel <= 0 || cond.blownTires === true);
    if (!retired) continue;
    raceState.retiredCars.set(i, {
      lap:     c.lapState?.lapNumber ?? 0,
      arc:     c.derived?.racingLineArc ?? 0,
      simTime: world.simTime,
    });
    raceState.finishedCars.add(i);
    c._raceFinished = true;
    // Stamp `_retired` so traffic helpers skip this car. Without it,
    // a wreck sitting on the racing line is still returned as the
    // "nearest car ahead" — under caution that dominoes the entire
    // bunch to a stop behind it; outside caution the AI's follow-
    // match likewise drags pace cars to the wreck's speed instead of
    // peeling around. Cleared in the un-retirement branch above when
    // a pit stop restores the car.
    c._retired = true;
    if (c.pitState) c.pitState.serviced = false;
    // Full-course caution trigger. Only during RACING — a retirement
    // in COOLDOWN is just "didn't finish their cool-down lap" and
    // doesn't warrant a yellow. The all-DNF short-circuit below clears
    // this if the new retirement actually empties the field.
    //
    // Final-lap exception: if the leader is already on the final lap
    // under green, don't throw a fresh yellow — battles to the line
    // shouldn't be neutered because someone parked it behind. An
    // already-active caution still extends (the existing race-end-
    // under-caution short-circuit will hold the yellow to the flag,
    // but at least clean-up reflects the new incident).
    if (raceState.phase === RACE_PHASES.RACING) {
      const leaderIdx = findCautionLeaderIdx(raceState, world);
      const leaderLap = leaderIdx >= 0
        ? (cars[leaderIdx].lapState?.lapNumber ?? 0)
        : 0;
      const onFinalLap = leaderLap >= raceState.totalLaps;
      if (!onFinalLap || raceState.caution.active) {
        triggerOrExtendCaution(raceState, i, world.simTime);
      }
    }
  }

  // Three-phase caution machine. Each phase reads up-to-date world
  // state — leader index can change tick-to-tick if the on-track
  // order shuffles, but each phase only cares about the *current*
  // leader. retiredCars is left intact across the whole machine so
  // the final classification still credits the wrecks' progress.
  if (raceState.caution.active) {
    // ---- Phase 1: cleanup → recovery ------------------------------
    // Once the cleanup timer expires, recover the wrecks (they vanish
    // from render + traffic + collisions) but DO NOT drop the green.
    // The flag stays yellow; cars stay paced. Idempotent — re-running
    // the loop is safe, and a new incident added after this point
    // gets recovered when sim time crosses the (extended) endsAt.
    if (world.simTime >= raceState.caution.endsAt) {
      for (const inc of raceState.caution.incidents) {
        const c = cars[inc.carIdx];
        if (c && !c._recovered) c._recovered = true;
      }
    }

    // ---- Phase 2: post-cleanup → "GREEN NEXT TIME BY" warn --------
    // Anchor `baseLeaderLap` at the moment cleanup finishes. The next
    // time the leader's lap counter ticks up we fire the warn — but
    // ONLY if the lap-bump looks like a real forward S/F crossing
    // (leader is now far from the S/F line just crossed, distToSF
    // ≈ totalLen). Pit-out crossings also bump lapNumber but emerge
    // close to S/F, so without this gate a car that pits during the
    // caution can fire a spurious warn.
    if (!raceState.caution.warned && world.simTime >= raceState.caution.endsAt) {
      const leaderIdx = findCautionLeaderIdx(raceState, world);
      if (leaderIdx >= 0) {
        const leader    = cars[leaderIdx];
        const leaderLap = leader.lapState?.lapNumber ?? 0;
        if (raceState.caution.baseLeaderLap === -1) {
          raceState.caution.baseLeaderLap = leaderLap;
        } else if (leaderLap > raceState.caution.baseLeaderLap) {
          const totalLen = world.geometry?.racingLine?.totalLength ?? 0;
          const distToSF = distanceToSF(leader, world);
          if (totalLen > 0 && Number.isFinite(distToSF) && distToSF >= totalLen * 0.5) {
            // Race-end-under-caution short-circuit. If this S/F crossing
            // puts the leader on the final lap (or past it), there's no
            // green-flag racing left — "green next time by" would mean
            // green at the checkered, which is meaningless. Race finishes
            // under yellow + white. Leave `warned` false so the warn pill
            // never appears and phase 3 never tries to drop green.
            if (leaderLap >= raceState.totalLaps) {
              raceState.caution.baseLeaderLap = leaderLap;
            } else {
              raceState.caution.warned        = true;
              raceState.caution.warnLeaderLap = leaderLap;
              raceState.caution.warnedAt      = world.simTime;
            }
          } else {
            // Lap bumped but the car is nowhere near "just past S/F".
            // Re-anchor and wait for a real S/F crossing.
            raceState.caution.baseLeaderLap = leaderLap;
          }
        }
      }
    }

    // ---- Phase 3: warning lap → green flag ------------------------
    // Drop green as the leader nears S/F on the warning lap. Three
    // gates have to all hold:
    //   1. distToSF inside the green-flag-lead window (~90m).
    //   2. Leader is on the warning lap (or has progressed past it
    //      via a pit-out lap bump — >= warnLeaderLap covers both).
    //   3. At least 80% of a pace-lap-time has elapsed since the warn
    //      fired. This is the safety belt: even if the on-track
    //      leader changes mid-warning-lap (e.g. the original leader
    //      pits), the new leader can't drop the green until enough
    //      sim-time has passed for *somebody* to plausibly have
    //      driven a near-full lap.
    if (raceState.caution.warned) {
      const leaderIdx = findCautionLeaderIdx(raceState, world);
      if (leaderIdx >= 0) {
        const leader    = cars[leaderIdx];
        const leaderLap = leader.lapState?.lapNumber ?? 0;
        const distToSF  = distanceToSF(leader, world);
        const totalLen  = world.geometry?.racingLine?.totalLength ?? 0;
        const minWarnElapsed = totalLen > 0
          ? (totalLen / CAUTION_PACE_SPEED_MS) * 0.8
          : 0;
        const elapsedSinceWarn = world.simTime - (raceState.caution.warnedAt ?? world.simTime);
        if (
          leaderLap >= raceState.caution.warnLeaderLap
          && distToSF <= CAUTION_GREEN_FLAG_LEAD_M
          && elapsedSinceWarn >= minWarnElapsed
        ) {
          raceState.caution.active = false;
          raceState.flag = 'green';
          // Open the no-overtake grace window. AI controllers read this
          // and force wantsOvertake=false until the timestamp passes.
          raceState.greenFlagGraceUntil = world.simTime + CAUTION_GRACE_PERIOD_S;
        }
      }
    }
  }

  // Per-tick caution pace stamping. Done after caution start/end is
  // settled so this tick's stamps reflect this tick's caution status.
  // Cars read `_cautionPaceTarget` next tick (controllers run at the
  // top of the tick loop, advanceRaceState at the bottom).
  if (raceState.caution.active) {
    applyCautionPace(raceState, world);
  } else {
    clearCautionPace(world);
  }

  // All-DNF short-circuit: if every car has retired before anyone can
  // take the checkered, end the race immediately under a red flag.
  // Without this the phase machine sits in RACING forever — the
  // cooldown trip is gated on a leader S/F crossing, which can't
  // happen with no cars still running. Red rather than checkered
  // because the scheduled distance was never completed.
  if (raceState.phase === RACE_PHASES.RACING
   && raceState.finishedCars.size >= cars.length) {
    raceState.phase      = RACE_PHASES.FINISHED;
    raceState.flag       = 'red';
    raceState.finishedAt = world.simTime;
    return;
  }

  if (raceState.phase === RACE_PHASES.RACING) {
    // Find the current leader — biggest (lapNumber, racingLineArc) tuple.
    // Normalize arc to S/F (see applyCautionPace for the rationale): a
    // car crossing the racing-line seam keeps its lapNumber but its
    // raw arc drops to ~0, so the unnormalized key briefly collapses
    // and the wrong car gets identified as the leader for one or two
    // ticks. Harmless mid-race (the COOLDOWN trip only fires when
    // someone hits lap > totalLaps), but cheap to make correct.
    let leaderIdx = -1;
    let leaderKey = -1;
    const racingLine = world.geometry.racingLine;
    const totalLen   = racingLine?.totalLength ?? 0;
    const arcSF      = racingLine?.arcSF ?? 0;
    for (let i = 0; i < cars.length; i++) {
      const c = cars[i];
      const lap = c.lapState?.lapNumber ?? 0;
      const arcRaw = c.derived?.racingLineArc ?? 0;
      const arc = totalLen > 0
        ? ((arcRaw - arcSF + totalLen) % totalLen)
        : 0;
      const key = lap * Math.max(totalLen, 1) + arc;
      if (key > leaderKey) { leaderKey = key; leaderIdx = i; }
    }
    if (leaderIdx < 0) return;

    // Leader has just crossed S/F to "start" lap totalLaps + 1 ⇒
    // they've completed totalLaps flying laps ⇒ checkered for them.
    const leaderCar = cars[leaderIdx];
    const leaderLap = leaderCar.lapState?.lapNumber ?? 0;
    if (leaderLap > raceState.totalLaps) {
      raceState.phase      = RACE_PHASES.COOLDOWN;
      raceState.finishedAt = world.simTime;
      raceState.finishOrder.push(leaderIdx);
      raceState.finishedCars.add(leaderIdx);
      // Leader stamps too — `_raceFinished` is what the AI controller
      // reads to switch to cool-down behavior, and `_finishLapAt` is
      // the lap they were on at flag drop (used for the no-pit
      // cool-down-lap-complete trigger). The trailing-cars branch
      // below stamps these for everyone else.
      leaderCar._raceFinished = true;
      leaderCar._finishLapAt  = leaderLap - 1;
      // Sim-seconds stamp at the moment each car crosses the finish
      // line. Used by the results modal to compute time gaps to leader
      // (this car) — other cars get theirs in the COOLDOWN branch
      // below as they cross. Retirees keep _finishedAt undefined so
      // the results table can render their lap count instead of a gap.
      leaderCar._finishedAt   = world.simTime;
      // Clear the per-visit serviced flag so the cool-down pit
      // entry triggers a fresh box stop. Without this, a car that
      // pitted earlier in the race would coast through pit lane
      // because the pitting → boxStopped transition keys on
      // !ps.serviced. The race-over short-circuit in updatePitPhases
      // then keeps them parked.
      if (leaderCar.pitState) leaderCar.pitState.serviced = false;
    }
    return;
  }

  if (raceState.phase === RACE_PHASES.COOLDOWN) {
    // A not-yet-finished car finishes when their NEXT S/F crossing
    // bumps lapNumber past _finishLapAt — the lap they were on when
    // the leader took the flag. Retirees are handled at the top of
    // this function, so this loop only watches for clean finishers.
    for (let i = 0; i < cars.length; i++) {
      if (raceState.finishedCars.has(i)) continue;
      const c = cars[i];
      if (c._finishLapAt == null) {
        c._finishLapAt = c.lapState?.lapNumber ?? 0;
        continue;
      }
      const lap = c.lapState?.lapNumber ?? 0;
      if (lap > c._finishLapAt) {
        raceState.finishOrder.push(i);
        raceState.finishedCars.add(i);
        c._raceFinished = true;
        c._finishedAt   = world.simTime;
        if (c.pitState) c.pitState.serviced = false;
      }
    }
    if (raceState.finishedCars.size >= cars.length) {
      raceState.phase = RACE_PHASES.FINISHED;
    }
  }
}

// Final race classification. Real motorsport convention: rank by
// LAPS COMPLETED first, then by crossing order within the same lap.
// A lead-lap finisher always outranks a lap-down car, even if the
// lap-down car physically crossed S/F sooner during the cool-down
// (which routinely happens — a car running 3 down with their cool-down
// lap closer to S/F than the 2nd-placed lead-lap car will hit S/F
// first, but they still finish behind 2nd). Without this sort, the
// lap-down car was being slotted into finishOrder ahead of the lead-
// lap car based on crossing time alone — visible bug for the user
// (Verstappen 2nd in T&S, classified 5th because three lapped cars
// crossed before him).
//
// Active finishers come first; retirees follow, sorted by progress at
// retirement (most laps first, then furthest arc). DNFs are credited
// for laps completed, not just listed after finishers in arbitrary
// order.
export function getFinalClassification(raceState, cars) {
  // Stable sort by _finishLapAt desc; we attach the original index so
  // ties resolve to S/F crossing order (leader first, then the
  // sequence cars were pushed onto finishOrder during cool-down).
  const finishers = raceState.finishOrder
    .map((idx, order) => ({
      idx,
      order,
      lap: cars?.[idx]?._finishLapAt ?? 0,
    }))
    .sort((a, b) => {
      if (b.lap !== a.lap) return b.lap - a.lap;
      return a.order - b.order;
    })
    .map(e => e.idx);
  const retired = Array.from(raceState.retiredCars.entries())
    .sort(([, a], [, b]) => {
      if (b.lap !== a.lap) return b.lap - a.lap;
      return b.arc - a.arc;
    })
    .map(([idx]) => idx);
  return [...finishers, ...retired];
}

// True iff manual-control input is allowed for `carIdx` in this race
// mode. God mode: any car. Solo: only the locked driver.
export function canControlCar(raceState, carIdx) {
  if (raceState.raceMode === RACE_MODES.GOD) return true;
  return raceState.soloCarIdx === carIdx;
}
